import type { UltrasoundRegion } from '@/models/dicom';

export type SpectralDopplerToolVariant = 'probe' | 'segment' | 'vti';

export type SpectralDopplerRole =
  | 'timeMs'
  | 'peakV'
  | 'meanV'
  | 'v1'
  | 'v2'
  | 'deltaV'
  | 'deltaT'
  | 'slope'
  | 'vti'
  | 'durationMs'
  | 'peakGradient'
  | 'meanGradient';

export interface SpectralCalibration {
  regionUuid: string;
  physicalDeltaX: number;
  physicalDeltaY: number;
  referencePixelX0: number;
  referencePixelY0: number;
  referencePixelPhysicalValueX: number;
  referencePixelPhysicalValueY: number;
  bboxMinX: number;
  bboxMinY: number;
  bboxMaxX: number;
  bboxMaxY: number;
  /** DICOM Doppler correction angle in degrees (0 if absent). Captured at creation. */
  dopplerCorrectionAngle: number;
  /** Whether the tool applies angle correction to reported velocities (v1 always true). */
  useAngleCorrection: boolean;
}

export interface SpectralDopplerValues {
  timeMs?: number;
  /** Peak (max abs) velocity in cm/s. */
  peakV_cmS?: number;
  meanV_cmS?: number;
  v1_cmS?: number;
  v2_cmS?: number;
  deltaV_cmS?: number;
  deltaT_sec?: number;
  /** Acceleration/deceleration slope in cm/s² (sign preserved). */
  slope_cmS2?: number;
  vti_cm?: number;
  durationMs?: number;
  peakGradient_mmHg?: number;
  meanGradient_mmHg?: number;
}

export interface SpectralDopplerResult {
  annotationUID: string;
  dicomUuid: string;
  frameIndex?: number;
  toolVariant: SpectralDopplerToolVariant;
  /** Handle points in image-pixel coords at completion (for rehydration / debugging). */
  pixelPoints: [number, number][];
  calibration: SpectralCalibration;
  values: SpectralDopplerValues;
  /** When the user clicked a specific report field first, the primary role flows there. */
  reportMeasurementClassUuid?: string;
  measurementLabel?: string;
  /** Which derived value corresponds to the report field the user explicitly selected. */
  primaryRole?: SpectralDopplerRole;
}

export interface SpectralRegionResolution {
  region: UltrasoundRegion | null;
  reason: 'ok' | 'no-region' | 'hz-units' | 'missing-calibration';
}

export interface SpectralDopplerAvailability {
  available: boolean;
  reason: 'no-region' | 'hz-units' | 'no-dicom' | null;
  /** The image has a spectral Doppler region, calibrated or not. */
  spectralRegion: boolean;
}

/** Custom event dispatched when a user clicks outside the spectral region's bbox. */
export const SPECTRAL_DOPPLER_OUT_OF_REGION = 'spectral-doppler:out-of-region';

export const SPECTRAL_DOPPLER_TOOL_NAMES = {
  PROBE: 'SpectralDopplerVelocityProbe',
  SEGMENT: 'SpectralDopplerVelocitySegment',
  VTI: 'SpectralDopplerVTITrace',
} as const;

export type SpectralDopplerToolName =
  (typeof SPECTRAL_DOPPLER_TOOL_NAMES)[keyof typeof SPECTRAL_DOPPLER_TOOL_NAMES];
